import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Home as HomeIcon,
  Wrench,
  RadioTower,
  ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";

const FIBER_IMG =
  "https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwc3BsaWNpbmd8ZW58MHx8fHwxNzkyOTU3NzcwfDA&ixlib=rb-4.1.0&q=85";
const HERO_IMG =
  "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxmaWJlciUyMG9wdGljJTIwdGVjaG5pY2lhbnxlbnwwfHx8fDE3OTI5NTc3NzB8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    icon: Building2,
    title: "Raccordement FTTO",
    text: "Raccordement fibre dédiée pour les entreprises, les sites tertiaires et les bâtiments professionnels : de la prise en charge technique à la mise en service.",
  },
  {
    icon: HomeIcon,
    title: "Raccordement FTTH",
    text: "Interventions sur les réseaux fibre jusqu'à l'abonné : raccordement des logements (segment D3), soudure, pose et raccordement des prises terminales.",
  },
  {
    icon: Wrench,
    title: "Interventions techniques",
    text: "Dépannage, reprise de raccordements, mesures et corrections sur infrastructures existantes, avec des techniciens équipés et expérimentés.",
  },
  {
    icon: RadioTower,
    title: "Infrastructures télécoms",
    text: "Travaux sur les infrastructures de réseau : chambres, points de mutualisation, baies de brassage, équipements associés, ainsi que travaux et dépose des réseaux cuivre.",
  },
];

const STEPS = [
  { num: "01", title: "Préparation", text: "Analyse du site, des accès et des contraintes techniques avant toute intervention." },
  { num: "02", title: "Réalisation", text: "Raccordement, soudure et pose réalisés par des techniciens terrain expérimentés." },
  { num: "03", title: "Contrôle & mesures", text: "Vérifications et mesures optiques pour garantir la conformité de la liaison." },
  { num: "04", title: "Restitution", text: "Compte rendu d'intervention documenté et transmis au donneur d'ordre." },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const TerrainFibre = () => (
  <>
    <Seo
      title="Le Terrain — Raccordement fibre optique FTTO & FTTH | ATLAS THD"
      description="Raccordement fibre optique FTTO et FTTH, interventions techniques et infrastructures télécoms : l'activité terrain d'ATLAS THD, sur toute la France."
    />

    {/* PAGE HERO */}
    <section className="border-b border-line bg-cloud" data-testid="terrain-hero">
      <div className="bg-blueprint">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-xs font-bold uppercase tracking-[0.25em] text-terrain"
            >
              Activité 01 — Le Terrain
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-4 font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
            >
              Raccordement fibre optique et infrastructures télécoms
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-steel"
            >
              ATLAS THD intervient sur le terrain pour raccorder, réparer et
              fiabiliser les infrastructures fibre optique. Des techniciens
              équipés, des méthodes éprouvées, des comptes rendus documentés.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-8"
            >
              <Link
                to="/contact"
                data-testid="terrain-devis-button"
                className="group inline-flex items-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white"
              >
                Parlez-nous de votre raccordement
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="border border-line bg-white p-3"
          >
            <img
              src={HERO_IMG}
              alt="Technicien ATLAS THD en intervention sur une infrastructure fibre optique"
              className="aspect-[4/3] w-full object-cover"
              data-testid="terrain-hero-image"
            />
          </motion.div>
        </div>
      </div>
    </section>

    {/* SERVICES */}
    <section className="bg-white py-20 lg:py-28" data-testid="terrain-services">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
            Nos prestations terrain
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            Quatre savoir-faire, un seul interlocuteur
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(10,25,47,0.25)] lg:p-10"
              data-testid={`terrain-service-${i}`}
            >
              <div className="flex items-center justify-between">
                <s.icon className="h-8 w-8 text-terrain" />
                <span className="font-display text-sm font-bold text-line">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-midnight">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-steel">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* MÉTHODE */}
    <section className="border-y border-line bg-cloud py-20 lg:py-28" data-testid="terrain-method">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="order-2 border border-line bg-white p-3 lg:order-1">
          <img
            src={FIBER_IMG}
            alt="Soudure et brassage de fibres optiques en baie"
            className="aspect-[4/3] w-full object-cover"
            data-testid="terrain-method-image"
          />
        </motion.div>
        <div className="order-1 lg:order-2">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
              Notre méthode
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
              Une intervention maîtrisée, du site au compte rendu
            </h2>
          </motion.div>
          <div className="mt-10 space-y-0 border-l-2 border-terrain">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative border-b border-line py-6 pl-8 first:pt-0"
                data-testid={`terrain-step-${i}`}
              >
                <span className="absolute -left-[9px] top-6 h-4 w-4 border-2 border-terrain bg-white first:top-0" />
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-terrain">
                  Étape {step.num}
                </p>
                <h3 className="mt-1 font-display text-lg font-bold text-midnight">{step.title}</h3>
                <p className="mt-1 text-sm text-steel">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <CtaBand title="Un raccordement fibre à planifier ?" />
  </>
);

export default TerrainFibre;
